// PWA utilities and service worker registration

// Save booking state before update
function saveBookingStateForUpdate(): void {
  try {
    const bookingState = {
      url: window.location.href,
      timestamp: Date.now(),
      formData: null as any,
      scrollPosition: window.pageYOffset
    };

    // Capture form data if in booking flow
    if (window.location.pathname.includes('/book') || 
        window.location.pathname.includes('/checkout')) {
      
      const forms = document.querySelectorAll('form');
      const formData: Record<string, any> = {};
      
      forms.forEach((form, index) => {
        const data = new FormData(form);
        const entries: Record<string, any> = {};
        for (const [key, value] of data.entries()) {
          entries[key] = value;
        }
        formData[`form_${index}`] = entries;
      });
      
      // Also capture input values
      const inputs = document.querySelectorAll('input, select, textarea');
      const inputData: Record<string, any> = {};
      inputs.forEach((input: any) => {
        if (input.id || input.name) {
          const key = input.id || input.name;
          inputData[key] = input.value || input.checked;
        }
      });
      
      bookingState.formData = { forms: formData, inputs: inputData };
    }

    sessionStorage.setItem('pwa-update-booking-state', JSON.stringify(bookingState));
    console.log('[PWA] Saved booking state for update:', bookingState);
  } catch (error) {
    console.error('[PWA] Failed to save booking state:', error);
  }
}

// Restore booking state after update
function restoreBookingStateAfterUpdate(): void {
  try {
    const savedState = sessionStorage.getItem('pwa-update-booking-state');
    if (!savedState) return;

    const state = JSON.parse(savedState);
    const timeDiff = Date.now() - state.timestamp;
    
    // Only restore if update happened within last 5 minutes
    if (timeDiff > 5 * 60 * 1000) {
      sessionStorage.removeItem('pwa-update-booking-state');
      return;
    }

    // Restore scroll position
    if (state.scrollPosition) {
      window.scrollTo(0, state.scrollPosition);
    }

    // Restore form data if available
    if (state.formData && state.formData.inputs) {
      setTimeout(() => {
        Object.entries(state.formData.inputs).forEach(([key, value]: [string, any]) => {
          const element = document.getElementById(key) || document.querySelector(`[name="${key}"]`);
          if (element) {
            if ((element as HTMLInputElement).type === 'checkbox' || (element as HTMLInputElement).type === 'radio') {
              (element as HTMLInputElement).checked = Boolean(value);
            } else {
              (element as HTMLInputElement).value = String(value || '');
            }
          }
        });
        
        // Show restoration notification
        showBookingStateRestoreNotification();
      }, 1000);
    }

    sessionStorage.removeItem('pwa-update-booking-state');
    console.log('[PWA] Restored booking state after update');
  } catch (error) {
    console.error('[PWA] Failed to restore booking state:', error);
  }
}

// Show notification about restored booking state
function showBookingStateRestoreNotification(): void {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #10b981;
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    z-index: 10001;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  `;
  notification.innerHTML = `✅ Booking form restored after update`;
  
  document.body.appendChild(notification);
  setTimeout(() => notification.remove(), 3000);
}

export const registerServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for SW updates
      });

      let refreshing = false;

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content is available
              console.log('New version available!');
              
              // Track update availability
              if (window.gtag) {
                window.gtag('event', 'pwa_update_available', {
                  event_category: 'PWA',
                  event_label: 'Update Available'
                });
              }
              
              // Show user-friendly update notification
              showUpdateNotification(() => {
                // Save current booking state before update
                saveBookingStateForUpdate();
                
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                if (!refreshing) {
                  refreshing = true;
                  window.location.reload();
                }
              });
            }
          });
        }
      });

      // Listen for service worker messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_ACTIVATED') {
          console.log('Service Worker activated, version:', event.data.version);
          if (!refreshing) {
            refreshing = true;
            window.location.reload();
          }
        }
      });

      // Auto-check for updates periodically
      setInterval(() => {
        registration.update();
      }, 60000); // Check every minute

      console.log('Service Worker registered successfully:', registration);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      
      // Track registration failures
      if (window.gtag) {
        window.gtag('event', 'pwa_sw_registration_failed', {
          event_category: 'PWA',
          event_label: 'Registration Failed',
          value: 1
        });
      }

      // Report to error logging system
      if (typeof window !== 'undefined') {
        import('../lib/error-reporting').then(({ errorTracker }) => {
          errorTracker.logError(error, {
            type: 'pwa_service_worker_registration',
            severity: 'medium',
            context: {
              userAgent: navigator.userAgent,
              url: window.location.href,
              protocol: window.location.protocol,
              serviceWorkerSupport: 'serviceWorker' in navigator
            }
          });
        }).catch(() => {
          // Silently fail if error reporting is unavailable
        });
      }
    }
  }
};

// Show update notification to user
function showUpdateNotification(onUpdate: () => void): void {
  // Create a native-looking update banner
  const banner = document.createElement('div');
  banner.id = 'pwa-update-banner';
  banner.innerHTML = `
    <div 
      role="alert"
      aria-labelledby="pwa-update-title"
      aria-describedby="pwa-update-description"
      style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: #5956F6;
        color: white;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
        font-size: 14px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      ">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span aria-hidden="true" style="font-size: 16px;">🔄</span>
        <div>
          <span id="pwa-update-title" style="font-weight: 600;">App Update Available</span>
          <span id="pwa-update-description" style="display: none;">A new version of Figgy is ready to install</span>
        </div>
      </div>
      <div style="display: flex; gap: 8px;">
        <button 
          id="pwa-update-btn" 
          type="button"
          aria-describedby="pwa-update-description"
          style="
            background: rgba(255,255,255,0.2);
            border: 1px solid rgba(255,255,255,0.3);
            color: white;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 500;
          ">Update Now</button>
        <button 
          id="pwa-dismiss-btn" 
          type="button"
          aria-label="Dismiss update notification"
          style="
            background: transparent;
            border: none;
            color: white;
            padding: 6px 8px;
            cursor: pointer;
            font-size: 16px;
            opacity: 0.7;
            border-radius: 4px;
          ">×</button>
      </div>
    </div>
  `;

  document.body.appendChild(banner);

  // Handle update button click
  const updateBtn = document.getElementById('pwa-update-btn');
  updateBtn?.addEventListener('click', () => {
    if (window.gtag) {
      window.gtag('event', 'pwa_update_accepted', {
        event_category: 'PWA',
        event_label: 'Update Accepted'
      });
    }
    onUpdate();
    banner.remove();
  });

  // Handle dismiss button click
  const dismissBtn = document.getElementById('pwa-dismiss-btn');
  dismissBtn?.addEventListener('click', () => {
    if (window.gtag) {
      window.gtag('event', 'pwa_update_dismissed', {
        event_category: 'PWA',
        event_label: 'Update Dismissed'
      });
    }
    banner.remove();
  });

  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (banner.parentNode) {
      banner.remove();
    }
  }, 30000);
}

export const unregisterServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(registration => registration.unregister()));
    console.log('Service Worker unregistered');
  }
};

export const checkForUpdates = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
    }
  }
};

export const isStandalone = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
};

export const isPWAInstallable = (): boolean => {
  return 'serviceWorker' in navigator && 
         'PushManager' in window &&
         'Notification' in window;
};

export const getInstallInstructions = (): string => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  
  if (isIOS && isSafari) {
    return 'Tap the Share button (□↗) and select "Add to Home Screen"';
  } else if (isIOS) {
    return 'Open in Safari, then tap Share → "Add to Home Screen"';
  } else if (isAndroid) {
    return 'Tap the menu (⋮) and select "Add to Home Screen" or "Install App"';
  } else {
    return 'Look for the install button in your browser\'s address bar';
  }
};

// Enhanced iOS PWA support
export const enhanceIOSPWA = (): void => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = (window.navigator as any).standalone === true;
  
  if (isIOS && isStandalone) {
    // iOS PWA is running, add specific enhancements
    
    // Fix viewport issues on iOS
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }
    
    // Add safe area CSS for notched iPhones
    const style = document.createElement('style');
    style.textContent = `
      :root {
        --safe-area-inset-top: env(safe-area-inset-top);
        --safe-area-inset-bottom: env(safe-area-inset-bottom);
        --safe-area-inset-left: env(safe-area-inset-left);
        --safe-area-inset-right: env(safe-area-inset-right);
      }
      
      .ios-safe-top { padding-top: var(--safe-area-inset-top); }
      .ios-safe-bottom { padding-bottom: var(--safe-area-inset-bottom); }
      .ios-safe-left { padding-left: var(--safe-area-inset-left); }
      .ios-safe-right { padding-right: var(--safe-area-inset-right); }
    `;
    document.head.appendChild(style);
    
    // Prevent bounce scrolling
    document.body.style.overscrollBehavior = 'none';
    
    // Track iOS PWA usage
    if (window.gtag) {
      window.gtag('event', 'pwa_ios_standalone_launch', {
        event_category: 'PWA',
        event_label: 'iOS Standalone Launch'
      });
    }
  }
};

// SEO-friendly service worker registration
export const registerServiceWorkerSEOFriendly = async (): Promise<void> => {
  // Only register after page load to avoid blocking initial render
  if (document.readyState === 'loading') {
    await new Promise(resolve => {
      document.addEventListener('DOMContentLoaded', resolve);
    });
  }
  
  // Wait for critical resources to load
  await new Promise(resolve => {
    if (document.readyState === 'complete') {
      resolve(undefined);
    } else {
      window.addEventListener('load', () => resolve(undefined));
    }
  });
  
  // Additional delay to ensure page is fully interactive
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Now register the service worker
  return registerServiceWorker();
};

// Check if current environment supports PWA features
export const getPWASupport = (): {
  serviceWorker: boolean;
  manifest: boolean;
  installPrompt: boolean;
  pushNotifications: boolean;
  backgroundSync: boolean;
} => {
  return {
    serviceWorker: 'serviceWorker' in navigator,
    manifest: 'serviceWorker' in navigator, // Proxy for manifest support
    installPrompt: 'BeforeInstallPromptEvent' in window || /Android/.test(navigator.userAgent),
    pushNotifications: 'PushManager' in window && 'Notification' in window,
    backgroundSync: 'serviceWorker' in navigator && 'SyncManager' in window
  };
};
